import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import Icon from '@components/Icon';
import { Tooltip, TooltipContent, TooltipTrigger } from '@components/ui/tooltip';
import { SavedViewsNavSectionController, useIsSavedViewActive } from '@features/saved-views';
import clsx from 'clsx';

import Nav from '../routes';

import type { MenuNavigation } from './menu';

export const navTooltipClasses = 'bg-info-900 rounded-[5px] px-[11px] py-2 text-xs text-white';

const variantClasses = {
  desktop: { link: 'text-sm font-medium', icon: 'pt-0.5' },
  mobile: { link: 'text-base font-medium capitalize', icon: '' },
};

interface MenuNavItemsProps {
  navigation: MenuNavigation[];
  isActive: (path: Nav) => boolean;
  variant: keyof typeof variantClasses;
  isCollapsed: boolean;
}

export const MenuNavItems = ({ navigation, isActive, variant, isCollapsed }: MenuNavItemsProps) => {
  const isSavedViewActive = useIsSavedViewActive();
  const classes = variantClasses[variant];
  const working = navigation.filter((item) => item.group === 'working');
  const support = navigation.filter((item) => item.group === 'support');

  const renderItem = (item: MenuNavigation) => {
    const isSubmissionsItem = item.path === Nav.Submissions;
    const itemActive = isActive(item.path) && !(isSubmissionsItem && isSavedViewActive);

    const link = (
      <Link
        to={item.path}
        aria-current={itemActive ? 'page' : undefined}
        aria-label={item.name}
        className={clsx(
          itemActive ? 'bg-info-900 text-white' : 'text-info-300 hover:bg-info-700 hover:text-white',
          'group flex items-center rounded-md px-2 py-2',
          classes.link,
        )}
      >
        <div
          className={clsx(
            'flex h-6 w-6 shrink-0 items-center justify-center',
            itemActive ? 'text-info-300' : 'text-info-400 group-hover:text-info-300',
            classes.icon,
          )}
        >
          <Icon name={item.icon} />
        </div>
        {!isCollapsed && (
          <span className={clsx('whitespace-nowrap', variant === 'desktop' ? 'ml-3' : 'ml-4')}>{item.name}</span>
        )}
      </Link>
    );

    return (
      <Fragment key={item.path}>
        {isCollapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>{link}</TooltipTrigger>
            <TooltipContent side="right" className={navTooltipClasses}>
              {item.name}
            </TooltipContent>
          </Tooltip>
        ) : (
          link
        )}
        {isSubmissionsItem && <SavedViewsNavSectionController isCollapsed={isCollapsed} />}
      </Fragment>
    );
  };

  return (
    <>
      {working.map(renderItem)}
      {/* Only a real boundary between two populated groups — a plain user sees Contracts and
          nothing in `support`, and a trailing rule with nothing under it reads as broken. */}
      {working.length > 0 && support.length > 0 && (
        <div className={clsx('bg-info-200 my-3 h-px', isCollapsed ? 'w-full' : 'w-[230px]')} role="separator" />
      )}
      {support.map(renderItem)}
    </>
  );
};
